import { filterClasses } from "@swivl-mono/helpers";
import { AnimatePresence, type Transition, motion } from "motion/react";
import { ReactNode, useRef } from "react";
import { IoClose } from "react-icons/io5";

import useAutoFocus from "../Hooks/useAutoFocus";
import useKeyPressAction from "../Hooks/useKeyPressAction";
import { useClickOutside } from "../Hooks/useOutsideClick";

export enum PanelPosition {
  LEFT = "left",
  RIGHT = "right"
}

interface PanelProps {
  children?: ReactNode;
  isOpen?: boolean;
  onToggle?: () => void;
  position?: PanelPosition;
  /** If returns true, outside click will not close the panel */
  shouldIgnoreOutsideClick?: (event: MouseEvent) => boolean;
  title?: string;
  width?: string;
  /** Override the slide-in/out animation. Defaults to `{ duration: 0.3 }`. */
  transition?: Transition;
}

const Panel = ({
  children,
  isOpen,
  onToggle,
  position = PanelPosition.RIGHT,
  shouldIgnoreOutsideClick,
  title,
  width,
  transition = { duration: 0.3 }
}: PanelProps) => {
  const panelRef = useRef<HTMLDivElement>(null);
  useAutoFocus(panelRef, [isOpen]);

  useClickOutside(
    panelRef,
    () => {
      if (isOpen && onToggle) {
        onToggle();
      }
    },
    shouldIgnoreOutsideClick
  );

  useKeyPressAction({
    callback: () => {
      if (isOpen && onToggle) {
        onToggle();
      }
    },
    key: "Escape"
  });

  return (
    <AnimatePresence mode="wait">
      {isOpen && (
        <motion.div
          className={filterClasses(
            "fixed top-12 z-100 h-[calc(100vh-48px)] bg-grey-50 shadow-lg outline-none",
            position === PanelPosition.LEFT ? "left-0" : "right-0",
            !width && "w-1/3"
          )}
          style={width ? { width } : undefined}
          initial={{ x: position === PanelPosition.LEFT ? "-100%" : "100%", opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: position === PanelPosition.LEFT ? "-100%" : "100%", opacity: 0 }}
          transition={transition}
          ref={panelRef}
          aria-modal="true"
          aria-labelledby="panel-title"
          role="dialog"
          tabIndex={-1}
        >
          <div className="relative h-full">
            <div id="panel-title" className="sr-only">
              {title || "Panel"}
            </div>

            {/* close icon */}
            <button
              aria-label="Close panel"
              onClick={onToggle}
              className={filterClasses(
                "absolute top-[13px] right-3 z-30 flex size-6 cursor-pointer items-center justify-center rounded-xs text-black",
                "hover:bg-grey-50 focus-visible:text-orange-500 active:text-yellow-500",
                "transition-colors duration-150 ease-in-out"
              )}
            >
              <IoClose className="size-4" />
            </button>

            {children}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export { Panel };
